import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Table, TableBody, TableHead, Toolbar, Typography, CircularProgress,
    TablePagination, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, MenuItem, Box, FormControl, InputLabel, Select,
    InputAdornment, TableSortLabel, Checkbox, FormControlLabel, Divider, Tabs, Tab
} from "@mui/material";
import { Search, FilterList, Block, CheckCircle, Add, Visibility, Edit } from "@mui/icons-material";

// Ensure these imports match your project structure
import PageHelp from "../components/common/PageHelp";
import { getStaffList, getStaffDetails, createStaff, updateStaff } from "../services/staffAdminService";
import { StaffUser, StaffRole, StaffStatus } from "../types/staff";
import { StyledTableContainer, StyledTableCell, StyledTableRow, StyledPaper } from "../assets/styles/tableStyle";
import FancyContextMenu, { ContextMenuAction } from "../components/FancyContextMenu";
import { useCan } from "../auth/Can";
import { useCurrentStaffId } from "../auth/AuthContext";
import { GRANTABLE_PERMISSIONS, isEffectivelyEnabled, setEffective } from "../utils/permissionOverride";
import { subtreeIdSet } from "../utils/staffTree";
import { humanizeEnum } from "../utils/enumLabels";
import StaffReportingTree from "./staff/StaffReportingTree";
import StaffPerformancePanel from "./staff/StaffPerformancePanel";

type MuiColor = "success" | "error" | "warning" | "primary" | "info" | "default";

const getStatusColor = (status: StaffStatus): MuiColor => {
    switch (status) {
        case StaffStatus.ACTIVE: return "success";
        case StaffStatus.INACTIVE: return "warning";
        case StaffStatus.SUSPENDED: return "error";
        default: return "default";
    }
};

const StaffAdminPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { can } = useCan();
    const canManageStaff = can('staff:manage');
    // Who is editing — used to stop someone taking their OWN dashboard login away.
    const currentStaffId = useCurrentStaffId();

    // Data State
    const [staffList, setStaffList] = useState<StaffUser[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);

    // The WHOLE staff list (unpaginated, org-sized): feeds the reporting tree, the Reports-to
    // selector, and manager-name resolution in the table. Refreshed after every save.
    const [allStaff, setAllStaff] = useState<StaffUser[]>([]);
    const [tab, setTab] = useState<'staff' | 'reporting' | 'performance'>('staff');

    const loadAllStaff = async () => {
        try {
            const res = await getStaffList({ page: 1, limit: 500 });
            setAllStaff(res.data.data || []);
        } catch (error) {
            console.error("Failed to fetch full staff list:", error);
        }
    };
    useEffect(() => { loadAllStaff(); }, []);

    const staffNameOf = (id: number | null | undefined): string => {
        if (id == null) return "—";
        const s = allStaff.find((x) => x.id === id);
        return s ? `${s.firstName} ${s.lastName || ""}`.trim() : `#${id}`;
    };

    // Pagination & Filters
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);
    const [searchTerm, setSearchTerm] = useState<string>(searchParams.get('search') || "");
    const [roleFilter, setRoleFilter] = useState<string>("all");
    const [statusFilter, setStatusFilter] = useState<string>("all");

    // Modal & Form State
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [formLoading, setFormLoading] = useState<boolean>(false);
    const [formData, setFormData] = useState<Partial<StaffUser> & { password?: string }>({});

    // Context Menu State
    const [menu, setMenu] = useState<{ top: number; left: number } | null>(null);
    const [selectedRow, setSelectedRow] = useState<StaffUser | null>(null);

    // Fetch Logic
    const fetchStaff = async (ignoreFlag?: { current: boolean }) => {
        setLoading(true);
        try {
            const params = {
                page: page + 1,
                limit: rowsPerPage,
                search: searchTerm || undefined,
                role: roleFilter !== 'all' ? roleFilter : undefined,
                status: statusFilter !== 'all' ? statusFilter : undefined,
            };
            const res = await getStaffList(params);

            if (ignoreFlag?.current) return;

            setStaffList(res.data.data || []);
            setTotalCount(res.data.total || 0);
        } catch (error) {
            console.error("Failed to fetch staff:", error);
        } finally {
            if (!ignoreFlag?.current) setLoading(false);
        }
    };

    // Debounced Search Effect
    useEffect(() => {
        const ignoreFlag = { current: false };
        const delayDebounceFn = setTimeout(() => {
            const currentParams = new URLSearchParams(searchParams);
            if (searchTerm) currentParams.set('search', searchTerm);
            else currentParams.delete('search');

            setSearchParams(currentParams, { replace: true });
            fetchStaff(ignoreFlag);
        }, 300);

        return () => {
            clearTimeout(delayDebounceFn);
            ignoreFlag.current = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, searchTerm, roleFilter, statusFilter]);

    // Context Menu Handlers
    const handleContextMenu = (event: React.MouseEvent, row: StaffUser) => {
        event.preventDefault();
        setSelectedRow(row);
        setMenu({ top: event.clientY, left: event.clientX });
    };

    const handleCloseMenu = () => setMenu(null);

    // Form Modal Handlers
    const handleOpenCreate = () => {
        setFormData({ status: StaffStatus.ACTIVE, role: StaffRole.FIELD_AGENT });
        setIsEditMode(false);
        setModalOpen(true);
    };

    const handleOpenEdit = async (id: number) => {
        setFormLoading(true);
        setIsEditMode(true);
        setModalOpen(true);
        try {
            const res = await getStaffDetails(id);
            setFormData(res.data);
        } catch (error) {
            console.error("Failed to fetch staff details", error);
        } finally {
            setFormLoading(false);
        }
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setFormData({});
        setSelectedRow(null);
    };

    const handleFieldChange = (field: string, value: any) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleSaveForm = async () => {
        setFormLoading(true);
        try {
            if (isEditMode && formData.id) {
                // Strip out fields backend shouldn't update directly here
                const { id, createdAt, lastLoginAt, ...updatePayload } = formData;
                await updateStaff(id, updatePayload);
            } else {
                await createStaff(formData);
            }
            await fetchStaff();
            await loadAllStaff();
            handleCloseModal();
        } catch (error: any) {
            // Basic error handling assuming backend throws 409 for duplicates
            alert(error.response?.data?.message || "Failed to save staff member");
        } finally {
            setFormLoading(false);
        }
    };

    const handleToggleStatus = async (staff: StaffUser) => {
        setLoading(true);
        try {
            const newStatus = staff.status === StaffStatus.ACTIVE ? StaffStatus.SUSPENDED : StaffStatus.ACTIVE;
            await updateStaff(staff.id, { status: newStatus });
            await fetchStaff();
        } finally {
            setLoading(false);
        }
    };

    // Context Menu Configuration — staff write actions require staff:manage.
    const actions: ContextMenuAction[] = canManageStaff ? [
        {
            label: "Edit Staff Member",
            icon: <Edit color="info" fontSize="small" />,
            onClick: () => { if (selectedRow) handleOpenEdit(selectedRow.id); },
            color: "info",
        },
        {
            dividerBefore: true,
            label: selectedRow?.status === StaffStatus.ACTIVE ? "Suspend Account" : "Activate Account",
            icon: selectedRow?.status === StaffStatus.ACTIVE
                ? <Block color="error" fontSize="small" />
                : <CheckCircle color="success" fontSize="small" />,
            onClick: () => { if (selectedRow) handleToggleStatus(selectedRow); },
            color: selectedRow?.status === StaffStatus.ACTIVE ? "error" : "success",
        }
    ] : [];

    return (
        <StyledPaper sx={{ maxWidth: "98vw", width: "100%", margin: '0 auto', my: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 3, pt: 2 }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ flex: 1 }}>
                    <Tab label="Staff & Internal Users" value="staff" />
                    <Tab label="Reporting Structure" value="reporting" />
                    <Tab label="Performance" value="performance" />
                </Tabs>
                <PageHelp k="staff" />
            </Box>

            {tab === 'performance' ? (
                <Box sx={{ px: 3, py: 2 }}>
                    <StaffPerformancePanel staff={allStaff} />
                </Box>
            ) : tab === 'reporting' ? (
                <Box sx={{ px: 3, py: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Who reports to whom. Click anyone to edit them, including their "Reports to".
                    </Typography>
                    <StaffReportingTree staff={allStaff} onSelect={handleOpenEdit} />
                </Box>
            ) : (
            <>
            <Toolbar sx={{ justifyContent: 'flex-end', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        placeholder="Search Name, Phone, Email..."
                        variant="outlined"
                        size="small"
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start"><Search fontSize="small" color="action" /></InputAdornment>
                            ),
                        }}
                        sx={{ minWidth: 260 }}
                    />

                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Role</InputLabel>
                        <Select
                            value={roleFilter}
                            label="Role"
                            onChange={(e) => { setRoleFilter(e.target.value); setPage(0); }}
                            startAdornment={<FilterList fontSize="small" sx={{ mr: 1, color: 'action.active' }} />}
                        >
                            <MenuItem value="all">All Roles</MenuItem>
                            <MenuItem value={StaffRole.SUPER_ADMIN}>Super Admin</MenuItem>
                            <MenuItem value={StaffRole.REGION_MANAGER}>Region Manager</MenuItem>
                            <MenuItem value={StaffRole.CITY_MANAGER}>City Manager</MenuItem>
                            <MenuItem value={StaffRole.FIELD_AGENT}>Field Agent</MenuItem>
                            <MenuItem value={StaffRole.VERIFICATION_OPS}>Verification Ops</MenuItem>
                            <MenuItem value={StaffRole.SUPPORT_AGENT}>Support Agent</MenuItem>
                            <MenuItem value={StaffRole.MARKETING}>Marketing</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={statusFilter}
                            label="Status"
                            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                        >
                            <MenuItem value="all">All Status</MenuItem>
                            <MenuItem value={StaffStatus.ACTIVE}>Active</MenuItem>
                            <MenuItem value={StaffStatus.SUSPENDED}>Suspended</MenuItem>
                        </Select>
                    </FormControl>

                    {canManageStaff && (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<Add />}
                            onClick={handleOpenCreate}
                        >
                            Onboard Staff
                        </Button>
                    )}
                </Box>
            </Toolbar>

            {loading && !staffList.length ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : (
                <StyledTableContainer>
                    <Table sx={{ minWidth: 1000 }}>
                        <TableHead>
                            <StyledTableRow>
                                <StyledTableCell>ID</StyledTableCell>
                                <StyledTableCell>First Name</StyledTableCell>
                                <StyledTableCell>Last Name</StyledTableCell>
                                <StyledTableCell>Username</StyledTableCell>
                                <StyledTableCell>Email</StyledTableCell>
                                <StyledTableCell>Phone</StyledTableCell>
                                <StyledTableCell>Role</StyledTableCell>
                                <StyledTableCell>Reports To</StyledTableCell>
                                <StyledTableCell>Status</StyledTableCell>
                                <StyledTableCell>Created At</StyledTableCell>
                            </StyledTableRow>
                        </TableHead>
                        <TableBody>
                            {staffList.map((staff) => (
                                <StyledTableRow
                                    key={staff.id}
                                    hover
                                    sx={{ cursor: "pointer" }}
                                    onContextMenu={e => handleContextMenu(e, staff)}
                                    onClick={() => handleOpenEdit(staff.id)}
                                >
                                    <StyledTableCell>{staff.id}</StyledTableCell>
                                    <StyledTableCell>{staff.firstName}</StyledTableCell>
                                    <StyledTableCell>{staff.lastName || "-"}</StyledTableCell>
                                    <StyledTableCell>{staff.username || "-"}</StyledTableCell>
                                    <StyledTableCell>{staff.email}</StyledTableCell>
                                    <StyledTableCell>{staff.phoneNumber}</StyledTableCell>
                                    <StyledTableCell>
                                        <Chip label={humanizeEnum(staff.role)} size="small" color="primary" variant="outlined" />
                                    </StyledTableCell>
                                    <StyledTableCell>{staffNameOf(staff.managerId)}</StyledTableCell>
                                    <StyledTableCell>
                                        <Chip label={humanizeEnum(staff.status)} color={getStatusColor(staff.status)} size="small" />
                                    </StyledTableCell>
                                    <StyledTableCell>{new Date(staff.createdAt).toLocaleDateString()}</StyledTableCell>
                                </StyledTableRow>
                            ))}
                            {staffList.length === 0 && !loading && (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={10} align="center">
                                        <Typography color="textSecondary" sx={{ py: 3 }}>No staff members found.</Typography>
                                    </StyledTableCell>
                                </StyledTableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50]}
                        component="div"
                        count={totalCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                    />
                </StyledTableContainer>
            )}
            </>
            )}

            {/* Create / Edit Modal */}
            <Dialog open={modalOpen} onClose={handleCloseModal} fullWidth maxWidth="sm" disableEnforceFocus>
                <DialogTitle>{isEditMode ? "Edit Staff Member" : "Onboard New Staff"}</DialogTitle>
                <DialogContent dividers>
                    {formLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
                    ) : (
                        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 2, pt: 1 }}>
                            <TextField
                                label="First Name"
                                required
                                value={formData.firstName || ""}
                                onChange={e => handleFieldChange("firstName", e.target.value)}
                            />
                            <TextField
                                label="Last Name"
                                value={formData.lastName || ""}
                                onChange={e => handleFieldChange("lastName", e.target.value)}
                            />
                            {/* Email and the web credentials go together: app-only staff sign in
                                with phone + OTP and often have no work address at all. */}
                            <TextField
                                label="Phone Number"
                                placeholder="10-digit number"
                                helperText="How they sign in to the Manager App"
                                value={formData.phoneNumber || ""}
                                onChange={e => handleFieldChange("phoneNumber", e.target.value)}
                            />
                            <TextField
                                label="Username"
                                helperText="Only for a web dashboard login"
                                value={formData.username || ""}
                                onChange={e => handleFieldChange("username", e.target.value)}
                            />
                            <TextField
                                label="Email"
                                type="email"
                                required={!!formData.username}
                                helperText={formData.username ? "Required for a web login" : "Optional"}
                                value={formData.email || ""}
                                onChange={e => handleFieldChange("email", e.target.value)}
                                sx={{ gridColumn: 'span 2' }}
                            />

                            {!isEditMode && !!formData.username && (
                                <TextField
                                    label="Temporary Password"
                                    type="password"
                                    required
                                    value={formData.password || ""}
                                    onChange={e => handleFieldChange("password", e.target.value)}
                                    sx={{ gridColumn: 'span 2' }}
                                />
                            )}

                            <TextField
                                select
                                label="Role"
                                required
                                value={formData.role || StaffRole.FIELD_AGENT}
                                onChange={e => handleFieldChange("role", e.target.value)}
                            >
                                <MenuItem value={StaffRole.SUPER_ADMIN}>Super Admin</MenuItem>
                                <MenuItem value={StaffRole.REGION_MANAGER}>Region Manager</MenuItem>
                                <MenuItem value={StaffRole.CITY_MANAGER}>City Manager</MenuItem>
                                <MenuItem value={StaffRole.FIELD_AGENT}>Field Agent</MenuItem>
                                <MenuItem value={StaffRole.VERIFICATION_OPS}>Verification Ops</MenuItem>
                                <MenuItem value={StaffRole.SUPPORT_AGENT}>Support Agent</MenuItem>
                                <MenuItem value={StaffRole.MARKETING}>Marketing</MenuItem>
                            </TextField>

                            <TextField
                                select
                                label="Status"
                                value={formData.status || StaffStatus.ACTIVE}
                                onChange={e => handleFieldChange("status", e.target.value)}
                            >
                                <MenuItem value={StaffStatus.ACTIVE}>Active</MenuItem>
                                <MenuItem value={StaffStatus.INACTIVE}>Inactive</MenuItem>
                                <MenuItem value={StaffStatus.SUSPENDED}>Suspended</MenuItem>
                            </TextField>

                            {/* Reporting line. Own subtree is excluded client-side (assigning me
                                under my own team is the cycle the backend rejects); the server
                                remains the authority and its message surfaces via the save alert. */}
                            <TextField
                                select
                                label="Reports to"
                                value={formData.managerId ?? ''}
                                onChange={e => handleFieldChange(
                                    "managerId",
                                    e.target.value === '' ? null : Number(e.target.value)
                                )}
                                sx={{ gridColumn: 'span 2' }}
                                helperText="Whose team this member belongs to. Their numbers roll up into that person's Manager App dashboard."
                            >
                                <MenuItem value="">— Nobody (top level) —</MenuItem>
                                {(() => {
                                    const excluded = isEditMode && formData.id
                                        ? subtreeIdSet(allStaff, formData.id)
                                        : new Set<number>();
                                    return allStaff
                                        .filter((s) => !excluded.has(s.id))
                                        .map((s) => (
                                            <MenuItem key={s.id} value={s.id}>
                                                {s.firstName} {s.lastName || ''} ({humanizeEnum(s.role)})
                                            </MenuItem>
                                        ));
                                })()}
                            </TextField>

                            {/* Manager App permissions, shown as their EFFECTIVE state: role
                                defaults (nobody below Super Admin holds any of these — owner
                                decision 2026-08-16) plus this member's grant/revoke override.
                                Unticking a default writes a revoke for just this person. */}
                            <Box sx={{ gridColumn: 'span 2', mt: 1 }}>
                                <Divider sx={{ mb: 1.5 }} />
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                    Manager App permissions
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    Effective for this person: role defaults plus their personal override. Suspending the account overrides everything.
                                </Typography>
                                {GRANTABLE_PERMISSIONS.map(p => {
                                    // Taking your own dashboard login away logs you out with no way
                                    // back in — the server refuses the last holder outright, this
                                    // stops the likelier self-inflicted case before the round trip.
                                    const lockingSelfOut = p.key === 'dashboard:access'
                                        && isEditMode
                                        && formData.id != null
                                        && formData.id === currentStaffId;
                                    return (
                                    <Box key={p.key} sx={{ mb: 0.5 }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    disabled={!canManageStaff || lockingSelfOut}
                                                    checked={isEffectivelyEnabled(formData.role, formData.permissions, p.key)}
                                                    onChange={e => handleFieldChange(
                                                        "permissions",
                                                        setEffective(formData.role, formData.permissions, p.key, e.target.checked)
                                                    )}
                                                />
                                            }
                                            label={p.label}
                                        />
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -0.5 }}>
                                            {lockingSelfOut
                                                ? 'You cannot remove your own dashboard login. Ask another super admin to do it.'
                                                : p.help}
                                        </Typography>
                                    </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal}>Cancel</Button>
                    {canManageStaff && (
                        <Button
                            variant="contained"
                            onClick={handleSaveForm}
                            disabled={formLoading || !formData.firstName || !formData.email || !formData.role}
                        >
                            {isEditMode ? "Save Changes" : "Create Staff"}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <FancyContextMenu
                actions={actions}
                open={Boolean(menu)}
                onClose={handleCloseMenu}
                anchorPosition={menu}
                title={selectedRow?.firstName ? `${selectedRow.firstName} ${selectedRow.lastName || ''}` : "Actions"}
            />
        </StyledPaper>
    );
};

export default StaffAdminPage;